// mocksAnalysis.js - Datos mock para el análisis de diagnósticos médicos

export const diagnosisAnalysisData = {
  "syndrome-coronario-agudo": {
    id: "syndrome-coronario-agudo",
    name: "Síndrome Coronario Agudo",
    code: "I20-I25",
    description: "El Síndrome Coronario Agudo (SCA) representa un espectro de condiciones que incluyen angina inestable, infarto de miocardio sin elevación del ST (IMSEST) e infarto de miocardio con elevación del ST (IMEST). Es causado por una reducción súbita del flujo sanguíneo al corazón.",
    snomedCode: "394659003",
    
    // Criterios diagnósticos
    diagnosticCriteria: {
      description: "Según la Sociedad Europea de Cardiología (2023), los criterios para SCA incluyen:",
      criteria: [
        "Dolor o molestia torácica típicos (opresión, pesadez, irradiación)",
        "Cambios electrocardiográficos (elevación/depresión del ST, inversión de onda T)",
        "Elevación de biomarcadores cardiacos (troponinas)",
        "Factores de riesgo cardiovascular presentes"
      ],
      currentStatus: "Criterios parcialmente cumplidos (requiere pruebas complementarias)"
    },

    // Diagnóstico diferencial
    differentialDiagnosis: [
      {
        condition: "Síndrome Coronario Agudo",
        characteristics: "Dolor opresivo retroesternal, factores de riesgo CV, responde a nitroglicerina",
        probability: 85
      },
      {
        condition: "Tromboembolismo Pulmonar",
        characteristics: "Disnea súbita, dolor pleurítico, factores de riesgo para TEP",
        probability: 62
      },
      {
        condition: "Pericarditis Aguda",
        characteristics: "Dolor que varía con postura/respiración, frote pericárdico, antecedente viral",
        probability: 45
      },
      {
        condition: "Disección Aórtica",
        characteristics: "Dolor intenso 'desgarrante', súbito, irradiado a espalda, asimetría de pulsos",
        probability: 30
      },
      {
        condition: "Reflujo Gastroesofágico",
        characteristics: "Dolor urente, relación con comidas, mejora con antácidos",
        probability: 20
      }
    ],

    // Análisis de síntomas
    symptomAnalysis: {
      present: [
        { symptom: "Dolor torácico", percentage: 85, description: "65% en SCA" },
        { symptom: "Dificultad para respirar", percentage: 70, description: "70% en SCA" },
        { symptom: "Náuseas", percentage: 35, description: "35% en SCA" },
        { symptom: "Sudoración", percentage: 50, description: "50% en SCA" },
        { symptom: "Fatiga", percentage: 30, description: "30% en SCA" }
      ],
      absent: [
        { symptom: "Dolor irradiado al brazo", percentage: 70, description: "70% en SCA" },
        { symptom: "Sensación de opresión", percentage: 80, description: "80% en SCA" },
        { symptom: "Palpitaciones", percentage: 20, description: "20% en SCA" }
      ],
      undetermined: [
        { symptom: "Ansiedad", percentage: 30, description: "30% en SCA" },
        { symptom: "Mareo", percentage: 15, description: "15% en SCA" },
        { symptom: "Antecedentes de enfermedad coronaria", percentage: null, description: null },
        { symptom: "Factores de riesgo cardiovascular", percentage: null, description: null }
      ]
    },

    // Pruebas recomendadas
    recommendedTests: [
      {
        category: "Laboratorio",
        tests: [
          { name: "Troponinas cardiacas", urgency: "STAT", reason: "Marcador específico de daño miocárdico" },
          { name: "CK-MB", urgency: "STAT", reason: "Complementa evaluación de daño cardiaco" },
          { name: "Gasometría arterial", urgency: "Urgente", reason: "Evaluar estado ácido-base y oxigenación" },
          { name: "Hemograma completo", urgency: "Rutina", reason: "Descartar anemia como causa secundaria" }
        ]
      },
      {
        category: "Imagenología",
        tests: [
          { name: "Electrocardiograma", urgency: "STAT", reason: "Identificar cambios isquémicos agudos" },
          { name: "Ecocardiograma", urgency: "Urgente", reason: "Evaluar función ventricular y anomalías regionales" },
          { name: "Radiografía de tórax", urgency: "Urgente", reason: "Descartar complicaciones pulmonares" },
          { name: "Angiografía coronaria", urgency: "Según criterios", reason: "Gold standard para diagnóstico definitivo" }
        ]
      }
    ],

    // Referencias y guías
    references: [
      {
        title: "Guía ESC 2023 para el manejo del síndrome coronario agudo",
        authors: "Sociedad Europea de Cardiología",
        journal: "European Heart Journal",
        year: 2023,
        doi: "10.1093/eurheartj/ehad191"
      },
      {
        title: "Consenso Argentino de Síndrome Coronario Agudo",
        authors: "Sociedad Argentina de Cardiología",
        journal: "Revista Argentina de Cardiología",
        year: 2022,
        doi: "10.7775/rac.v90.i3.20567"
      }
    ],

    // Datos para visualización
    visualization: {
      prevalenceByAge: [
        { ageRange: "18-30", prevalence: 0.5 },
        { ageRange: "31-40", prevalence: 2.1 },
        { ageRange: "41-50", prevalence: 8.3 },
        { ageRange: "51-60", prevalence: 15.7 },
        { ageRange: "61-70", prevalence: 28.4 },
        { ageRange: ">70", prevalence: 35.2 }
      ],
      riskFactors: [
        { factor: "Hipertensión", percentage: 65 },
        { factor: "Diabetes", percentage: 45 },
        { factor: "Tabaquismo", percentage: 55 },
        { factor: "Dislipidemia", percentage: 70 },
        { factor: "Obesidad", percentage: 40 },
        { factor: "Sedentarismo", percentage: 50 }
      ],
      mortality: {
        overall: 8.5,
        withTreatment: 4.2,
        withoutTreatment: 15.8
      }
    }
  },

  "angina-inestable": {
    id: "angina-inestable",
    name: "Angina Inestable",
    code: "I20.0",
    description: "Forma de síndrome coronario agudo caracterizada por dolor torácico de origen isquémico que ocurre en reposo o con mínimo esfuerzo, sin elevación de biomarcadores cardiacos.",
    snomedCode: "25106000",

    diagnosticCriteria: {
      description: "Según ACC/AHA 2023, los criterios para angina inestable incluyen:",
      criteria: [
        "Dolor torácico típico de origen coronario",
        "Inicio reciente (<2 meses) o empeoramiento del patrón",
        "Ocurrencia en reposo o con mínimo esfuerzo",
        "Troponinas normales o mínimamente elevadas",
        "Sin elevación del segmento ST"
      ],
      currentStatus: "Angina inestable probable (requiere estratificación de riesgo)"
    },

    differentialDiagnosis: [
      {
        condition: "Angina Inestable",
        characteristics: "Dolor típico, patrón cambiante, troponinas normales",
        probability: 80
      },
      {
        condition: "Infarto de Miocardio NSTEMI",
        characteristics: "Dolor similar pero con elevación de troponinas",
        probability: 65
      },
      {
        condition: "Angina Estable",
        characteristics: "Dolor predecible con esfuerzo, patrón estable",
        probability: 40
      },
      {
        condition: "Espasmo Coronario",
        characteristics: "Dolor en reposo, respuesta a calcioantagonistas",
        probability: 35
      }
    ],

    symptomAnalysis: {
      present: [
        { symptom: "Dolor torácico opresivo", percentage: 90, description: "95% en angina inestable" },
        { symptom: "Dolor en reposo", percentage: 75, description: "80% en angina inestable" },
        { symptom: "Cambio en patrón de dolor", percentage: 85, description: "90% en angina inestable" },
        { symptom: "Disnea", percentage: 60, description: "65% en angina inestable" }
      ],
      absent: [
        { symptom: "Elevación de troponinas", percentage: 5, description: "Criterio diferencial con NSTEMI" },
        { symptom: "Cambios ST elevados", percentage: 2, description: "Criterio diferencial con STEMI" },
        { symptom: "Síntomas vegetativos severos", percentage: 15, description: "Menos común que en IAM" }
      ],
      undetermined: [
        { symptom: "Sudoración", percentage: 40, description: "40% en angina inestable" },
        { symptom: "Náuseas", percentage: 25, description: "25% en angina inestable" },
        { symptom: "Irradiación a brazo", percentage: 50, description: "50% en angina inestable" }
      ]
    },

    recommendedTests: [
      {
        category: "Laboratorio",
        tests: [
          { name: "Troponinas seriadas", urgency: "STAT", reason: "Diferenciar de NSTEMI" },
          { name: "D-dímero", urgency: "Urgente", reason: "Descartar embolia pulmonar" },
          { name: "Péptido natriurético", urgency: "Urgente", reason: "Evaluar función cardiaca" }
        ]
      },
      {
        category: "Imagenología",
        tests: [
          { name: "ECG seriados", urgency: "STAT", reason: "Monitorear cambios isquémicos" },
          { name: "Ecocardiograma", urgency: "Urgente", reason: "Evaluar función ventricular" },
          { name: "Coronariografía", urgency: "Según riesgo", reason: "Estratificación invasiva" }
        ]
      }
    ],

    references: [
      {
        title: "2020 ESC Guidelines for the Management of Acute Coronary Syndromes",
        authors: "European Society of Cardiology",
        journal: "European Heart Journal",
        year: 2020,
        doi: "10.1093/eurheartj/ehaa575"
      }
    ],

    visualization: {
      prevalenceByAge: [
        { ageRange: "18-40", prevalence: 1.2 },
        { ageRange: "41-50", prevalence: 4.8 },
        { ageRange: "51-60", prevalence: 12.3 },
        { ageRange: "61-70", prevalence: 22.1 },
        { ageRange: ">70", prevalence: 28.7 }
      ],
      riskFactors: [
        { factor: "Hipertensión", percentage: 70 },
        { factor: "Diabetes", percentage: 50 },
        { factor: "Tabaquismo", percentage: 60 },
        { factor: "Dislipidemia", percentage: 75 },
        { factor: "Historia familiar", percentage: 45 }
      ],
      mortality: {
        overall: 3.2,
        withTreatment: 1.8,
        withoutTreatment: 8.5
      }
    }
  },

  "pericarditis-aguda": {
    id: "pericarditis-aguda",
    name: "Pericarditis Aguda",
    code: "I30",
    description: "Inflamación del pericardio caracterizada por dolor torácico pleurítico, frote pericárdico y cambios electrocardiográficos típicos.",
    snomedCode: "3238004",

    diagnosticCriteria: {
      description: "Según ESC 2015, los criterios para pericarditis aguda incluyen (≥2 requeridos):",
      criteria: [
        "Dolor torácico pleurítico típico",
        "Frote pericárdico",
        "Cambios electrocardiográficos característicos",
        "Derrame pericárdico nuevo o empeorado"
      ],
      currentStatus: "Pericarditis aguda confirmada (2+ criterios presentes)"
    },

    differentialDiagnosis: [
      {
        condition: "Pericarditis Aguda",
        characteristics: "Dolor pleurítico, frote, cambios ECG difusos",
        probability: 85
      },
      {
        condition: "Infarto de Miocardio",
        characteristics: "Dolor opresivo, elevación focal ST, troponinas+",
        probability: 30
      },
      {
        condition: "Embolia Pulmonar",
        characteristics: "Disnea súbita, factores de riesgo, D-dímero+",
        probability: 25
      },
      {
        condition: "Pleuritis",
        characteristics: "Dolor pleurítico, derrame pleural, sin frote",
        probability: 40
      }
    ],

    symptomAnalysis: {
      present: [
        { symptom: "Dolor torácico pleurítico", percentage: 95, description: "95% en pericarditis" },
        { symptom: "Mejora con inclinación anterior", percentage: 80, description: "85% en pericarditis" },
        { symptom: "Empeora con decúbito", percentage: 75, description: "80% en pericarditis" },
        { symptom: "Frote pericárdico", percentage: 60, description: "65% en pericarditis" }
      ],
      absent: [
        { symptom: "Dolor opresivo típico", percentage: 10, description: "Característico de SCA" },
        { symptom: "Respuesta a nitroglicerina", percentage: 5, description: "Típico de angina" },
        { symptom: "Cambios focales en ECG", percentage: 15, description: "Sugiere IAM" }
      ],
      undetermined: [
        { symptom: "Fiebre", percentage: 40, description: "45% en pericarditis viral" },
        { symptom: "Disnea", percentage: 35, description: "40% en pericarditis" },
        { symptom: "Síntomas virales previos", percentage: 60, description: "65% en pericarditis viral" }
      ]
    },

    recommendedTests: [
      {
        category: "Laboratorio",
        tests: [
          { name: "PCR y VSG", urgency: "Urgente", reason: "Marcadores de inflamación" },
          { name: "Troponinas", urgency: "STAT", reason: "Descartar miocarditis asociada" },
          { name: "Hemograma completo", urgency: "Rutina", reason: "Evaluar respuesta inflamatoria" },
          { name: "Función renal", urgency: "Rutina", reason: "Baseline antes de AINEs" }
        ]
      },
      {
        category: "Imagenología",
        tests: [
          { name: "Electrocardiograma", urgency: "STAT", reason: "Cambios típicos de pericarditis" },
          { name: "Ecocardiograma", urgency: "Urgente", reason: "Evaluar derrame pericárdico" },
          { name: "Radiografía de tórax", urgency: "Urgente", reason: "Evaluar silueta cardiaca" },
          { name: "TC/RM cardiaca", urgency: "Según criterios", reason: "Evaluar pericardio y miocardio" }
        ]
      }
    ],

    references: [
      {
        title: "2015 ESC Guidelines for the Diagnosis and Management of Pericardial Diseases",
        authors: "European Society of Cardiology",
        journal: "European Heart Journal",
        year: 2015,
        doi: "10.1093/eurheartj/ehv318"
      },
      {
        title: "Pericarditis: Diagnosis and Management",
        authors: "American Heart Association",
        journal: "Circulation",
        year: 2017,
        doi: "10.1161/CIR.0000000000000475"
      }
    ],

    visualization: {
      prevalenceByAge: [
        { ageRange: "18-30", prevalence: 8.5 },
        { ageRange: "31-40", prevalence: 12.3 },
        { ageRange: "41-50", prevalence: 9.7 },
        { ageRange: "51-60", prevalence: 7.2 },
        { ageRange: ">60", prevalence: 5.8 }
      ],
      etiologies: [
        { etiology: "Idiopática/Viral", percentage: 85 },
        { etiology: "Bacteriana", percentage: 5 },
        { etiology: "Tuberculosa", percentage: 4 },
        { etiology: "Neoplásica", percentage: 3 },
        { etiology: "Autoinmune", percentage: 2 },
        { etiology: "Otras", percentage: 1 }
      ],
      mortality: {
        overall: 1.2,
        uncomplicated: 0.2,
        withTamponade: 15.0
      }
    }
  },

  "neumonia-adquirida-comunidad": {
    id: "neumonia-adquirida-comunidad",
    name: "Neumonía Adquirida en la Comunidad",
    code: "J12-J18",
    description: "Infección aguda del parénquima pulmonar que se desarrolla en personas que no han estado hospitalizadas en las últimas 48-72 horas.",
    snomedCode: "233604007",
    
    diagnosticCriteria: {
      description: "Según ATS/IDSA 2019, los criterios para NAC incluyen:",
      criteria: [
        "Síntomas respiratorios agudos (tos, disnea, dolor pleurítico)",
        "Fiebre (>38°C) o hipotermia (<36°C)",
        "Infiltrado pulmonar nuevo en radiografía de tórax",
        "Leucocitosis o leucopenia significativa"
      ],
      currentStatus: "NAC confirmada (infiltrado pulmonar + síntomas)"
    },

    differentialDiagnosis: [
      {
        condition: "Neumonía Adquirida en Comunidad",
        characteristics: "Fiebre, tos productiva, infiltrado pulmonar, leucocitosis",
        probability: 85
      },
      {
        condition: "Bronquitis Aguda",
        characteristics: "Tos persistente, sin fiebre alta, radiografía normal",
        probability: 45
      },
      {
        condition: "Embolia Pulmonar",
        characteristics: "Disnea súbita, dolor pleurítico, factores de riesgo",
        probability: 25
      },
      {
        condition: "Exacerbación de EPOC",
        characteristics: "Antecedente de EPOC, aumento de disnea y expectoración",
        probability: 35
      },
      {
        condition: "Neumonía Atípica",
        characteristics: "Síntomas subagudos, tos seca, infiltrados intersticiales",
        probability: 40
      }
    ],

    symptomAnalysis: {
      present: [
        { symptom: "Tos", percentage: 85, description: "90% en NAC" },
        { symptom: "Fiebre", percentage: 80, description: "85% en NAC" },
        { symptom: "Disnea", percentage: 70, description: "75% en NAC" },
        { symptom: "Expectoración purulenta", percentage: 65, description: "70% en NAC" },
        { symptom: "Dolor pleurítico", percentage: 55, description: "60% en NAC" }
      ],
      absent: [
        { symptom: "Tos seca persistente", percentage: 30, description: "Más común en neumonía atípica" },
        { symptom: "Síntomas gastrointestinales", percentage: 20, description: "20% en Legionella" },
        { symptom: "Confusión mental", percentage: 15, description: "15% en ancianos" }
      ],
      undetermined: [
        { symptom: "Escalofríos", percentage: 50, description: "55% en NAC" },
        { symptom: "Fatiga", percentage: 70, description: "75% en NAC" },
        { symptom: "Cefalea", percentage: 25, description: "30% en NAC atípica" }
      ]
    },

    recommendedTests: [
      {
        category: "Laboratorio",
        tests: [
          { name: "Hemograma con diferencial", urgency: "STAT", reason: "Evaluar respuesta inflamatoria" },
          { name: "PCR y procalcitonina", urgency: "Urgente", reason: "Marcadores de inflamación bacteriana" },
          { name: "Hemocultivos", urgency: "Antes ATB", reason: "Identificación del patógeno" },
          { name: "Esputo para cultivo", urgency: "Antes ATB", reason: "Identificación del patógeno respiratorio" },
          { name: "Antígenos urinarios", urgency: "Urgente", reason: "Detección de S. pneumoniae y Legionella" }
        ]
      },
      {
        category: "Imagenología",
        tests: [
          { name: "Radiografía de tórax PA y lateral", urgency: "STAT", reason: "Confirmar infiltrado pulmonar" },
          { name: "TC de tórax", urgency: "Si complicaciones", reason: "Evaluar extensión y complicaciones" },
          { name: "Ecografía pulmonar", urgency: "Disponible", reason: "Alternativa en casos específicos" }
        ]
      }
    ],

    references: [
      {
        title: "Diagnosis and Treatment of Adults with Community-acquired Pneumonia",
        authors: "American Thoracic Society/IDSA",
        journal: "American Journal of Respiratory and Critical Care Medicine",
        year: 2019,
        doi: "10.1164/rccm.201908-1581ST"
      },
      {
        title: "British Thoracic Society Guidelines for Community Acquired Pneumonia",
        authors: "British Thoracic Society",
        journal: "Thorax",
        year: 2009,
        doi: "10.1136/thx.2009.121434"
      }
    ],

    visualization: {
      prevalenceByAge: [
        { ageRange: "18-30", prevalence: 1.2 },
        { ageRange: "31-50", prevalence: 2.8 },
        { ageRange: "51-65", prevalence: 8.5 },
        { ageRange: ">65", prevalence: 25.3 }
      ],
      pathogens: [
        { pathogen: "S. pneumoniae", percentage: 35 },
        { pathogen: "H. influenzae", percentage: 15 },
        { pathogen: "Atípicos", percentage: 25 },
        { pathogen: "Virus", percentage: 20 },
        { pathogen: "Otros", percentage: 5 }
      ],
      mortality: {
        overall: 5.2,
        outpatient: 1.5,
        hospitalized: 12.8
      }
    }
  },

  "reflujo-gastroesofagico": {
    id: "reflujo-gastroesofagico",
    name: "Enfermedad por Reflujo Gastroesofágico",
    code: "K21",
    description: "Condición crónica en la que el contenido gástrico regurgita hacia el esófago causando síntomas molestos y/o complicaciones.",
    snomedCode: "235595009",

    diagnosticCriteria: {
      description: "Según Montreal Consensus 2006, los criterios para ERGE incluyen:",
      criteria: [
        "Síntomas típicos (pirosis, regurgitación) ≥2 veces/semana",
        "Respuesta a inhibidores de bomba de protones",
        "Endoscopia con esofagitis o pH-metría anormal",
        "Ausencia de causas mecánicas de obstrucción"
      ],
      currentStatus: "ERGE probable (síntomas típicos + respuesta a IBP)"
    },

    differentialDiagnosis: [
      {
        condition: "ERGE",
        characteristics: "Pirosis, regurgitación, mejora con IBP, relación con comidas",
        probability: 80
      },
      {
        condition: "Dispepsia Funcional",
        characteristics: "Dolor epigástrico, saciedad precoz, sin reflujo típico",
        probability: 45
      },
      {
        condition: "Úlcera Péptica",
        characteristics: "Dolor epigástrico nocturno, mejora con alimentos",
        probability: 35
      },
      {
        condition: "Espasmo Esofágico",
        characteristics: "Dolor torácico, disfagia intermitente",
        probability: 25
      },
      {
        condition: "Cáncer Esofágico",
        characteristics: "Disfagia progresiva, pérdida de peso, edad avanzada",
        probability: 5
      }
    ],

    symptomAnalysis: {
      present: [
        { symptom: "Pirosis", percentage: 85, description: "85% en ERGE" },
        { symptom: "Regurgitación", percentage: 70, description: "75% en ERGE" },
        { symptom: "Dolor epigástrico", percentage: 60, description: "65% en ERGE" },
        { symptom: "Síntomas nocturnos", percentage: 50, description: "55% en ERGE" }
      ],
      absent: [
        { symptom: "Disfagia", percentage: 15, description: "15% en ERGE (sugiere complicaciones)" },
        { symptom: "Pérdida de peso", percentage: 10, description: "10% en ERGE (bandera roja)" },
        { symptom: "Odinofagia", percentage: 5, description: "5% en ERGE (sugiere esofagitis severa)" }
      ],
      undetermined: [
        { symptom: "Tos nocturna", percentage: 40, description: "45% en ERGE extraesofágico" },
        { symptom: "Disfonía", percentage: 25, description: "30% en ERGE extraesofágico" },
        { symptom: "Dolor torácico", percentage: 35, description: "40% en ERGE" }
      ]
    },

    recommendedTests: [
      {
        category: "Evaluación Inicial",
        tests: [
          { name: "Prueba terapéutica con IBP", urgency: "Primera línea", reason: "Diagnóstico y tratamiento empírico" },
          { name: "Escala de síntomas GERD-Q", urgency: "Rutina", reason: "Cuantificar síntomas" }
        ]
      },
      {
        category: "Pruebas Especializadas",
        tests: [
          { name: "Endoscopia digestiva alta", urgency: "Si banderas rojas", reason: "Evaluar esofagitis y complicaciones" },
          { name: "pH-metría 24h", urgency: "Si diagnóstico dudoso", reason: "Confirmar reflujo ácido" },
          { name: "Manometría esofágica", urgency: "Pre-quirúrgica", reason: "Evaluar motilidad esofágica" },
          { name: "Impedanciometría", urgency: "Casos complejos", reason: "Detectar reflujo no ácido" }
        ]
      }
    ],

    references: [
      {
        title: "Montreal Definition and Classification of Gastroesophageal Reflux Disease",
        authors: "Vakil N, van Zanten SV, et al.",
        journal: "American Journal of Gastroenterology",
        year: 2006,
        doi: "10.1111/j.1572-0241.2006.00630.x"
      },
      {
        title: "ACG Clinical Guidelines: Diagnosis and Management of GERD",
        authors: "American College of Gastroenterology",
        journal: "American Journal of Gastroenterology",
        year: 2022,
        doi: "10.14309/ajg.0000000000001538"
      }
    ],

    visualization: {
      prevalenceByAge: [
        { ageRange: "18-30", prevalence: 15.2 },
        { ageRange: "31-40", prevalence: 22.8 },
        { ageRange: "41-50", prevalence: 28.5 },
        { ageRange: "51-60", prevalence: 35.7 },
        { ageRange: ">60", prevalence: 42.3 }
      ],
      riskFactors: [
        { factor: "Obesidad", percentage: 60 },
        { factor: "Hernia hiatal", percentage: 45 },
        { factor: "Tabaquismo", percentage: 35 },
        { factor: "Alcohol", percentage: 30 },
        { factor: "Embarazo", percentage: 25 },
        { factor: "Ciertos medicamentos", percentage: 20 }
      ],
      severity: {
        mild: 65,
        moderate: 25,
        severe: 10
      }
    }
  },

  "tromboembolismo-pulmonar": {
    id: "tromboembolismo-pulmonar",
    name: "Tromboembolismo Pulmonar",
    code: "I26",
    description: "Obstrucción de una o más arterias pulmonares por émbolos, generalmente originados en el sistema venoso profundo.",
    snomedCode: "59282003",

    diagnosticCriteria: {
      description: "Según ESC 2019, los criterios para TEP incluyen:",
      criteria: [
        "Síntomas compatibles (disnea súbita, dolor pleurítico)",
        "Factores de riesgo para tromboembolismo venoso",
        "Elevación de D-dímero (en probabilidad baja-intermedia)",
        "Confirmación imagenológica (angioTC o gammagrafía V/Q)"
      ],
      currentStatus: "TEP de probabilidad intermedia (requiere confirmación imagenológica)"
    },

    differentialDiagnosis: [
      {
        condition: "Tromboembolismo Pulmonar",
        characteristics: "Disnea súbita, dolor pleurítico, factores de riesgo TEV, D-dímero elevado",
        probability: 75
      },
      {
        condition: "Neumonía",
        characteristics: "Tos, fiebre, infiltrado pulmonar, leucocitosis",
        probability: 45
      },
      {
        condition: "Infarto de Miocardio",
        characteristics: "Dolor torácico opresivo, cambios ECG, troponinas elevadas",
        probability: 35
      },
      {
        condition: "Neumotórax",
        characteristics: "Dolor pleurítico súbito, disnea, ausencia de murmullo vesicular",
        probability: 25
      },
      {
        condition: "Exacerbación de EPOC",
        characteristics: "Antecedente de EPOC, aumento de disnea y tos",
        probability: 40
      }
    ],

    symptomAnalysis: {
      present: [
        { symptom: "Disnea súbita", percentage: 80, description: "85% en TEP" },
        { symptom: "Dolor pleurítico", percentage: 65, description: "70% en TEP" },
        { symptom: "Taquipnea", percentage: 70, description: "75% en TEP" },
        { symptom: "Factores de riesgo TEV", percentage: 85, description: "90% en TEP" }
      ],
      absent: [
        { symptom: "Hemoptisis", percentage: 15, description: "20% en TEP" },
        { symptom: "Sincope", percentage: 10, description: "15% en TEP masivo" },
        { symptom: "Cianosis", percentage: 5, description: "10% en TEP severo" }
      ],
      undetermined: [
        { symptom: "Taquicardia", percentage: 60, description: "65% en TEP" },
        { symptom: "Fiebre", percentage: 25, description: "30% en TEP" },
        { symptom: "Dolor en pantorrilla", percentage: 40, description: "45% si TVP asociada" }
      ]
    },

    recommendedTests: [
      {
        category: "Laboratorio",
        tests: [
          { name: "D-dímero", urgency: "STAT", reason: "Marcador de fibrinólisis, alta sensibilidad" },
          { name: "Gasometría arterial", urgency: "STAT", reason: "Evaluar intercambio gaseoso" },
          { name: "Troponinas", urgency: "Urgente", reason: "Evaluar sobrecarga cardiaca derecha" },
          { name: "Péptido natriurético", urgency: "Urgente", reason: "Marcador de disfunción VD" }
        ]
      },
      {
        category: "Imagenología",
        tests: [
          { name: "AngioTC pulmonar", urgency: "STAT", reason: "Gold standard para diagnóstico" },
          { name: "Ecocardiograma", urgency: "Urgente", reason: "Evaluar función ventricular derecha" },
          { name: "Ecografía Doppler MMII", urgency: "Urgente", reason: "Buscar TVP asociada" },
          { name: "Gammagrafía V/Q", urgency: "Alternativa", reason: "Si contraindicación para TC" }
        ]
      }
    ],

    references: [
      {
        title: "2019 ESC Guidelines for Acute Pulmonary Embolism",
        authors: "European Society of Cardiology",
        journal: "European Heart Journal",
        year: 2019,
        doi: "10.1093/eurheartj/ehz405"
      },
      {
        title: "CHEST Guideline for Antithrombotic Therapy for VTE Disease",
        authors: "American College of Chest Physicians",
        journal: "Chest",
        year: 2021,
        doi: "10.1016/j.chest.2021.07.055"
      }
    ],

    visualization: {
      prevalenceByAge: [
        { ageRange: "18-30", prevalence: 0.8 },
        { ageRange: "31-40", prevalence: 1.5 },
        { ageRange: "41-50", prevalence: 3.2 },
        { ageRange: "51-60", prevalence: 6.8 },
        { ageRange: "61-70", prevalence: 12.5 },
        { ageRange: ">70", prevalence: 18.3 }
      ],
      riskFactors: [
        { factor: "Cirugía reciente", percentage: 45 },
        { factor: "Inmovilización", percentage: 35 },
        { factor: "Cáncer activo", percentage: 25 },
        { factor: "Trombofilia", percentage: 15 },
        { factor: "Anticonceptivos orales", percentage: 20 },
        { factor: "TEV previo", percentage: 30 }
      ],
      mortality: {
        overall: 8.5,
        treated: 3.2,
        untreated: 25.8,
        massive: 15.2
      }
    }
  }
};

// Datos adicionales para comparación y análisis
export const comparisonData = {
  symptomComparison: [
    {
      symptom: "Dolor torácico",
      conditions: [
        { name: "SCA", percentage: 85, type: "opresivo" },
        { name: "Pericarditis", percentage: 95, type: "pleurítico" },
        { name: "TEP", percentage: 65, type: "pleurítico" },
        { name: "ERGE", percentage: 60, type: "urente" }
      ]
    },
    {
      symptom: "Disnea",
      conditions: [
        { name: "SCA", percentage: 70, type: "esfuerzo" },
        { name: "NAC", percentage: 75, type: "reposo" },
        { name: "TEP", percentage: 85, type: "súbita" },
        { name: "Pericarditis", percentage: 40, type: "leve" }
      ]
    },
    {
      symptom: "Fiebre",
      conditions: [
        { name: "NAC", percentage: 85, type: "alta" },
        { name: "Pericarditis", percentage: 45, type: "moderada" },
        { name: "TEP", percentage: 30, type: "baja" },
        { name: "SCA", percentage: 10, type: "ausente" }
      ]
    }
  ],

  urgencyLevels: {
    "STAT": {
      description: "Inmediato (0-15 minutos)",
      conditions: ["SCA", "TEP masivo", "Tamponamiento"]
    },
    "Urgente": {
      description: "Urgente (15-60 minutos)",
      conditions: ["NAC severa", "Pericarditis", "TEP"]
    },
    "Rutina": {
      description: "No urgente (1-24 horas)",
      conditions: ["ERGE", "Seguimiento"]
    }
  },

  clinicalScores: {
    wells: {
      name: "Wells Score para TEP",
      parameters: [
        { parameter: "TVP probable", points: 3 },
        { parameter: "TEP más probable que diagnóstico alternativo", points: 3 },
        { parameter: "Taquicardia >100", points: 1.5 },
        { parameter: "Inmovilización/cirugía reciente", points: 1.5 },
        { parameter: "TEP/TVP previo", points: 1.5 },
        { parameter: "Hemoptisis", points: 1 },
        { parameter: "Cáncer", points: 1 }
      ],
      interpretation: {
        low: { range: "≤4", probability: "12%" },
        moderate: { range: "4.5-6", probability: "28%" },
        high: { range: ">6", probability: "59%" }
      }
    },
    
    grace: {
      name: "GRACE Score para SCA",
      parameters: [
        "Edad", "Frecuencia cardíaca", "Presión arterial sistólica",
        "Creatinina sérica", "Clase Killip", "Paro cardíaco",
        "Elevación ST", "Elevación troponinas"
      ],
      interpretation: {
        low: { range: "≤108", mortality: "<1%" },
        intermediate: { range: "109-140", mortality: "1-3%" },
        high: { range: ">140", mortality: ">3%" }
      }
    }
  }
};

// Configuración para visualizaciones
export const chartConfigs = {
  colors: {
    primary: "#3B82F6",
    secondary: "#10B981",
    warning: "#F59E0B",
    danger: "#EF4444",
    info: "#6366F1",
    success: "#22C55E"
  },
  
  themes: {
    medical: {
      background: "#F8FAFC",
      cardBg: "#FFFFFF",
      textPrimary: "#1E293B",
      textSecondary: "#64748B",
      border: "#E2E8F0"
    }
  }
};

// Extensiones para los datos mock existentes

// RF-12: Factores demográficos y antecedentes del paciente
export const demographicFactors = {
  age: {
    ranges: [
      { min: 18, max: 30, riskModifier: 0.2 },
      { min: 31, max: 50, riskModifier: 0.5 },
      { min: 51, max: 65, riskModifier: 1.0 },
      { min: 66, max: 80, riskModifier: 1.5 },
      { min: 81, max: 100, riskModifier: 2.0 }
    ]
  },
  gender: {
    male: { riskModifier: 1.2, conditions: ["SCA", "TEP"] },
    female: { riskModifier: 0.8, conditions: ["SCA"], protectiveFactors: ["premenopause"] }
  },
  riskFactors: {
    cardiovascular: [
      { factor: "Hipertensión", weight: 2, conditions: ["SCA", "Angina"] },
      { factor: "Diabetes", weight: 3, conditions: ["SCA", "Angina"] },
      { factor: "Tabaquismo", weight: 2, conditions: ["SCA", "NAC", "TEP"] },
      { factor: "Dislipidemia", weight: 2, conditions: ["SCA", "Angina"] },
      { factor: "Obesidad", weight: 1.5, conditions: ["SCA", "ERGE", "TEP"] },
      { factor: "Historia familiar", weight: 1.5, conditions: ["SCA"] }
    ],
    respiratory: [
      { factor: "EPOC", weight: 3, conditions: ["NAC", "TEP"] },
      { factor: "Asma", weight: 2, conditions: ["NAC"] },
      { factor: "Tabaquismo", weight: 2, conditions: ["NAC"] }
    ],
    thrombotic: [
      { factor: "Cirugía reciente", weight: 4, conditions: ["TEP"] },
      { factor: "Inmovilización", weight: 3, conditions: ["TEP"] },
      { factor: "Cáncer activo", weight: 4, conditions: ["TEP"] },
      { factor: "Anticonceptivos orales", weight: 2, conditions: ["TEP"] },
      { factor: "TEV previo", weight: 4, conditions: ["TEP"] }
    ]
  }
};

// RF-13: Jerarquía completa de SNOMED CT
export const snomedHierarchy = {
  "syndrome-coronario-agudo": {
    snomedCode: "394659003",
    hierarchy: {
      level1: {
        code: "404684003",
        name: "Hallazgo clínico",
        parent: null
      },
      level2: {
        code: "609096000",
        name: "Trastorno cardiovascular",
        parent: "404684003"
      },
      level3: {
        code: "49601007",
        name: "Trastorno del corazón",
        parent: "609096000"
      },
      level4: {
        code: "414545008",
        name: "Trastorno isquémico del corazón",
        parent: "49601007"
      },
      level5: {
        code: "394659003",
        name: "Síndrome coronario agudo",
        parent: "414545008"
      }
    },
    children: [
      {
        code: "25106000",
        name: "Angina inestable"
      },
      {
        code: "401303003",
        name: "Infarto agudo de miocardio"
      }
    ],
    relatedConcepts: [
      {
        code: "233819005",
        name: "Dolor torácico",
        relationship: "finding"
      },
      {
        code: "267038008",
        name: "Disnea",
        relationship: "finding"
      }
    ]
  },

  "pericarditis-aguda": {
    snomedCode: "3238004",
    hierarchy: {
      level1: {
        code: "404684003",
        name: "Hallazgo clínico",
        parent: null
      },
      level2: {
        code: "609096000",
        name: "Trastorno cardiovascular",
        parent: "404684003"
      },
      level3: {
        code: "49601007",
        name: "Trastorno del corazón",
        parent: "609096000"
      },
      level4: {
        code: "81817003",
        name: "Trastorno del pericardio",
        parent: "49601007"
      },
      level5: {
        code: "3238004",
        name: "Pericarditis aguda",
        parent: "81817003"
      }
    },
    children: [
      {
        code: "194828000",
        name: "Pericarditis viral"
      },
      {
        code: "56819005",
        name: "Pericarditis bacteriana"
      }
    ]
  },

  "neumonia-adquirida-comunidad": {
    snomedCode: "233604007",
    hierarchy: {
      level1: {
        code: "404684003",
        name: "Hallazgo clínico",
        parent: null
      },
      level2: {
        code: "609096000",
        name: "Trastorno del sistema respiratorio",
        parent: "404684003"
      },
      level3: {
        code: "50043002",
        name: "Trastorno del tracto respiratorio inferior",
        parent: "609096000"
      },
      level4: {
        code: "233604007",
        name: "Neumonía adquirida en la comunidad",
        parent: "50043002"
      }
    },
    children: [
      {
        code: "233607000",
        name: "Neumonía neumocócica"
      },
      {
        code: "233608005",
        name: "Neumonía atípica"
      }
    ]
  }
};

// Extensión para integrar factores demográficos en cada diagnóstico
export const extendedDiagnosisData = {
  "syndrome-coronario-agudo": {
    demographicAnalysis: {
      ageRisk: {
        optimalRange: { min: 45, max: 75 },
        currentRisk: "alto", // basado en edad del paciente
        riskFactorMultiplier: 1.8
      },
      genderRisk: {
        maleRisk: 1.5,
        femaleRisk: 0.8,
        currentGenderRisk: "alto" // si es masculino
      },
      applicableRiskFactors: [
        { factor: "Hipertensión", present: true, weight: 2 },
        { factor: "Diabetes", present: false, weight: 3 },
        { factor: "Tabaquismo", present: true, weight: 2 },
        { factor: "Historia familiar", present: null, weight: 1.5 }
      ],
      riskScore: {
        total: 8.5,
        interpretation: "Riesgo alto para SCA",
        recommendation: "Evaluación cardiológica inmediata"
      }
    },
    
    clinicalDecisionSupport: {
      algorithms: [
        {
          name: "HEART Score",
          components: ["Historia", "ECG", "Edad", "Factores de riesgo", "Troponinas"],
          score: 6,
          interpretation: "Riesgo intermedio",
          recommendation: "Observación y pruebas seriadas"
        },
        {
          name: "TIMI Risk Score",
          components: ["Edad ≥65", "≥3 factores de riesgo CV", "Estenosis coronaria conocida", "Desviación ST", "≥2 episodios anginosos", "Uso de aspirina", "Marcadores cardiacos elevados"],
          score: 4,
          interpretation: "Riesgo intermedio-alto",
          recommendation: "Estrategia invasiva temprana"
        }
      ]
    }
  },

  "tromboembolismo-pulmonar": {
    demographicAnalysis: {
      ageRisk: {
        optimalRange: { min: 40, max: 80 },
        currentRisk: "moderado",
        riskFactorMultiplier: 1.3
      },
      genderRisk: {
        pregnancyRisk: 5.0,
        oralContraceptiveRisk: 2.0,
        currentGenderRisk: "moderado"
      },
      applicableRiskFactors: [
        { factor: "Cirugía reciente", present: false, weight: 4 },
        { factor: "Inmovilización", present: true, weight: 3 },
        { factor: "Cáncer activo", present: false, weight: 4 },
        { factor: "TEV previo", present: null, weight: 4 }
      ]
    },
    
    clinicalDecisionSupport: {
      algorithms: [
        {
          name: "Wells Score",
          components: ["TVP probable", "TEP más probable", "Taquicardia", "Inmovilización", "TEP/TVP previo", "Hemoptisis", "Cáncer"],
          score: 4.5,
          interpretation: "Probabilidad intermedia",
          recommendation: "D-dímero + AngioTC si elevado"
        },
        {
          name: "Geneva Score",
          components: ["Edad", "TEP/TVP previo", "Cirugía", "Cáncer activo", "Dolor unilateral MMII", "Hemoptisis", "FC", "Dolor a palpación"],
          score: 5,
          interpretation: "Probabilidad intermedia",
          recommendation: "Confirmación imagenológica"
        }
      ]
    }
  }
};

// Función para calcular riesgo personalizado
export function calculatePersonalizedRisk(patientData, condition) {
  const baseRisk = diagnosisAnalysisData[condition].differentialDiagnosis
    .find(d => d.condition === diagnosisAnalysisData[condition].name)?.probability || 50;
  
  let riskModifier = 1;
  
  // Modificador por edad
  if (patientData.age) {
    const ageRange = demographicFactors.age.ranges
      .find(range => patientData.age >= range.min && patientData.age <= range.max);
    if (ageRange) {
      riskModifier *= ageRange.riskModifier;
    }
  }
  
  // Modificador por género
  if (patientData.gender) {
    const genderFactor = demographicFactors.gender[patientData.gender];
    if (genderFactor) {
      riskModifier *= genderFactor.riskModifier;
    }
  }
  
  // Modificador por factores de riesgo
  if (patientData.riskFactors) {
    patientData.riskFactors.forEach(factor => {
      const riskFactor = demographicFactors.riskFactors.cardiovascular
        .concat(demographicFactors.riskFactors.respiratory)
        .concat(demographicFactors.riskFactors.thrombotic)
        .find(rf => rf.factor === factor);
      
      if (riskFactor && riskFactor.conditions.includes(condition)) {
        riskModifier += (riskFactor.weight - 1) * 0.1;
      }
    });
  }
  
  return Math.min(Math.round(baseRisk * riskModifier), 100);
}

// Función para obtener recomendaciones personalizadas
export function getPersonalizedRecommendations(patientData, condition) {
  const personalizedRisk = calculatePersonalizedRisk(patientData, condition);
  const conditionData = diagnosisAnalysisData[condition];
  
  let urgencyLevel, recommendations;
  
  if (personalizedRisk >= 80) {
    urgencyLevel = "CRÍTICO";
    recommendations = [
      "Evaluación inmediata en servicio de urgencias",
      "Monitorización continua",
      "Preparación para intervención urgente"
    ];
  } else if (personalizedRisk >= 60) {
    urgencyLevel = "ALTO";
    recommendations = [
      "Evaluación urgente por especialista",
      "Pruebas diagnósticas inmediatas",
      "Considerar hospitalización"
    ];
  } else if (personalizedRisk >= 40) {
    urgencyLevel = "MODERADO";
    recommendations = [
      "Evaluación médica en 24-48 horas",
      "Pruebas diagnósticas programadas",
      "Seguimiento estrecho"
    ];
  } else {
    urgencyLevel = "BAJO";
    recommendations = [
      "Evaluación médica rutinaria",
      "Manejo conservador",
      "Seguimiento según evolución"
    ];
  }
  
  return {
    personalizedRisk,
    urgencyLevel,
    recommendations,
    specificTests: conditionData.recommendedTests
      .flatMap(category => category.tests)
      .filter(test => test.urgency === "STAT" && personalizedRisk >= 60)
      .concat(
        conditionData.recommendedTests
          .flatMap(category => category.tests)
          .filter(test => test.urgency === "Urgente" && personalizedRisk >= 40)
      )
  };
}

export default diagnosisAnalysisData;